import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PublishSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoHome?: () => void;
  title?: string;
  message?: string;
}

export const PublishSuccessModal: React.FC<PublishSuccessModalProps> = ({
  isOpen,
  onClose,
  onGoHome,
  title = 'Publish Successful',
  message = 'You have successfully published your article'
}) => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsAnimating(false);
    // Wait for exit animation before closing
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleGoHome = () => {
    setIsAnimating(false);
    // Wait for exit animation before closing
    setTimeout(() => {
      onClose();
      if (onGoHome) {
        onGoHome();
      } else {
        navigate('/admin/dashboard');
      }
    }, 200);
  };

  return (
    <div 
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center p-4 transition-opacity duration-300 ${
        isAnimating ? 'bg-opacity-30' : 'bg-opacity-0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden transition-all duration-300 ease-out ${
          isAnimating 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with illustration */}
        <div className="bg-gray-50 pt-5 flex items-center justify-center">
          <img 
            src="/post-icon.png" 
            alt="Publish Successful" 
            className={`w-[139px] h-auto object-contain transition-all duration-500 delay-100 ${
              isAnimating 
                ? 'opacity-100 scale-100 rotate-0' 
                : 'opacity-0 scale-90 rotate-[-5deg]'
            }`}
          />
        </div>
        
        {/* Modal Content */}
        <div className={`p-6 transition-all duration-300 delay-150 ${
          isAnimating 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-2'
        }`}>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
          <p className="text-sm text-gray-500 mb-6">{message}</p>
          
          {/* Action Button */}
          <button
            onClick={handleGoHome}
            className="w-full text-sm font-medium text-white bg-[#047857] rounded-lg px-4 py-2 hover:bg-[#059669] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishSuccessModal;

